import { Button, Label, Radio } from 'flowbite-react';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { hireAssessmentCandidate, rejectAssessmentCandidate } from 'src/apis/campusHiring';
import { useToast } from 'src/context/ToastProvider';
import UsersAvatarGroup from './UsersAvatarGroup';
import { useGetCampusHiringApplicationsWithCandidates } from 'src/hooks/campusHiring.hooks';
import { useParams } from 'react-router-dom';
import { GetCampusHiringApplicationsWithCandidatesAPIResponse } from 'types';
import { QueryState } from 'react-query/types/core/query';

const AssessmentUsersBulkAction = ({
	showModalUi = false,
	bulkSelectedCandidates,
}: {
	showModalUi?: boolean;
	bulkSelectedCandidates: string[];
}) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [localActionSelect, setLocalActionSelect] = useState('');
	const queryData: QueryState<GetCampusHiringApplicationsWithCandidatesAPIResponse, undefined> | undefined =
		queryClient.getQueryState(['getCampusHiringApplicationsWithCandidates'], {
			exact: false,
		});

	const hireCampusCandidateFn = useMutation(['hireCampusCandidate'], hireAssessmentCandidate, {
		onSuccess(data) {
			queryClient.refetchQueries(['getCampusHiringCandidates']);
			queryClient.refetchQueries({
				queryKey: ['getCampusHiringApplicationsWithCandidates'],
				exact: false, // This allows refetching of all matching queries
			});
			toast({
				title: 'Status updated successfully',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const rejectCampusCandidateFn = useMutation(['rejectCampusCandidate'], rejectAssessmentCandidate, {
		onSuccess(data) {
			queryClient.refetchQueries(['getCampusHiringCandidates']);
			queryClient.refetchQueries({
				queryKey: ['getCampusHiringApplicationsWithCandidates'],
				exact: false, // This allows refetching of all matching queries
			});
			toast({
				title: 'Status updated successfully',
				type: 'success',
			});
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				title: `Error occured ${errorMessage}`,
				type: 'error',
			});
		},
	});

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (localActionSelect === 'hire-candidates') {
			hireCampusCandidateFn.mutate({
				candidateIds: bulkSelectedCandidates,
			});
		} else if (localActionSelect === 'reject-candidates') {
			rejectCampusCandidateFn.mutate({
				candidateIds: bulkSelectedCandidates,
			});
		}
	};

	return !showModalUi ? (
		<div className="flex items-center gap-2 flex-wrap justify-center">
			<Button
				size="sm"
				color="light"
				isProcessing={hireCampusCandidateFn?.isLoading}
				disabled={!bulkSelectedCandidates.length || hireCampusCandidateFn?.isLoading}
				className="focus:ring-[1px] focus:ring-gray-400"
				onClick={() => {
					hireCampusCandidateFn.mutate({
						candidateIds: bulkSelectedCandidates,
					});
				}}
			>
				Hire Candidates
			</Button>
			<Button
				size="sm"
				color="light"
				isProcessing={rejectCampusCandidateFn?.isLoading}
				disabled={!bulkSelectedCandidates.length || rejectCampusCandidateFn?.isLoading}
				className="focus:ring-[1px] focus:ring-gray-400"
				onClick={() => {
					rejectCampusCandidateFn.mutate({
						candidateIds: bulkSelectedCandidates,
					});
				}}
			>
				Reject Candidates
			</Button>
		</div>
	) : (
		<>
			<div className="px-3 py-4">
				<UsersAvatarGroup
					candidates={
						queryData?.data?.campusHiringApplications?.map(e => ({
							name: e?.email || '',
							profileImageUrl: e?.profile?.profileImageUrl || e?.profile?.imageUrl || '',
						})) || []
					}
				/>

				<div className="h-[1px] bg-[#EBEBF0] my-[12px]"></div>
			</div>

			<div className="px-3 pb-4">
				<form onSubmit={handleSubmit}>
					<fieldset
						className="grid grid-cols-2 max-w-md gap-4"
						onChange={e => {
							const target = e.target as HTMLInputElement;
							if (target?.value) {
								setLocalActionSelect(target?.value);
							}
						}}
					>
						<div className="flex items-center gap-2">
							<Radio
								className="text-[#7777E7] focus:ring-[#7777E7]"
								id={'hireCandidates'}
								name="bulkAction"
								value={'hire-candidates'}
								checked={localActionSelect === 'hire-candidates'}
							/>
							<Label htmlFor={'hireCandidates'} className="text-sm font-[400] font-inter">
								Hire Candidates
							</Label>
						</div>
						<div className="flex items-center gap-2">
							<Radio
								className="text-[#7777E7] focus:ring-[#7777E7] disabled:opacity-45 disabled:bg-[#d2d2d2]"
								id={'rejectCandidates'}
								name="bulkAction"
								value={'reject-candidates'}
								checked={localActionSelect === 'reject-candidates'}
							/>
							<Label htmlFor={'rejectCandidates'} className="text-sm font-[400] font-inter">
								Reject Candidates
							</Label>
						</div>
					</fieldset>
					<Button
						type="submit"
						color="purplePlaced"
						className="bg-[#7777E7] w-full mt-[24px]"
						isProcessing={rejectCampusCandidateFn.isLoading || hireCampusCandidateFn?.isLoading}
						disabled={!localActionSelect || rejectCampusCandidateFn.isLoading || hireCampusCandidateFn?.isLoading}
					>
						{localActionSelect === 'reject-candidates'
							? 'Reject Candidates'
							: localActionSelect === 'hire-candidates'
							  ? 'Hire Candidates'
							  : 'Select'}
					</Button>
				</form>
			</div>
		</>
	);
};

export default AssessmentUsersBulkAction;
